import * as React from 'react';
import {
    Button,
    CreateButton,
    Datagrid,
    DateField,
    FunctionField,
    List,
    Pagination,
    TextField,
    TopToolbar,
    useDataProvider,
    useNotify,
    useRefresh,
    useTranslate,
} from 'react-admin';
import { Stack } from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import { downloadCampaignTemplate, parseCampaignWorkbook } from './campaignWorkbook';
import CustomEmpty from '../controls/CustomEmpty';
import CampaignExportButton from './CampaignExportButton';
import CampaignWorkbookButton from './CampaignWorkbookButton';
import CampaignListAside from './CampaignListAside';
import CampaignStatusField from './CampaignStatusField';
import CampaignResponseRateField from './CampaignResponseRateField';
import { formatCampaignWeekdays } from './campaignWeekdays';

const CampaignListActions = () => {
    const dataProvider = useDataProvider();
    const notify = useNotify();
    const refresh = useRefresh();
    const inputRef = React.useRef<HTMLInputElement>(null);
    const [busy, setBusy] = React.useState(false);

    const downloadTemplate = async () => {
        setBusy(true);
        try {
            await downloadCampaignTemplate();
        } catch (err) {
            console.error(err);
            notify('resources.campaign.template_error', { type: 'error' });
        } finally {
            setBusy(false);
        }
    };

    const importWorkbook = async (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        event.target.value = '';
        if (!file) return;
        setBusy(true);
        try {
            const data = await parseCampaignWorkbook(file);
            await dataProvider.create('campaign/import', { data });
            notify('resources.campaign.import_success', { type: 'success' });
            refresh();
        } catch (err) {
            console.error(err);
            notify(
                err instanceof Error
                    ? err.message
                    : 'Excel-tuonti epäonnistui.',
                { type: 'error' }
            );
        } finally {
            setBusy(false);
        }
    };

    return (
        <TopToolbar sx={{ mt: 3 }}>
            <Button
                label="resources.campaign.actions.template"
                onClick={downloadTemplate}
                disabled={busy}
            >
                <DownloadIcon />
            </Button>
            <Button
                label="resources.campaign.actions.import"
                onClick={() => inputRef.current?.click()}
                disabled={busy}
            >
                <UploadFileIcon />
            </Button>
            <input
                ref={inputRef}
                type="file"
                accept=".xlsx"
                hidden
                onChange={importWorkbook}
            />
            <CreateButton />
        </TopToolbar>
    );
};

const CampaignListEmpty = () => (
    <Stack spacing={2}>
        <CampaignListActions />
        <CustomEmpty />
    </Stack>
);

const CampaignList = () => {
    const translate = useTranslate();
    return (
        <List
            sort={{ field: 'start_date', order: 'DESC' }}
            perPage={50}
            pagination={<Pagination rowsPerPageOptions={[10, 25, 50, 100]} />}
            actions={<CampaignListActions />}
            aside={<CampaignListAside />}
            empty={<CampaignListEmpty />}
        >
            <Datagrid rowClick="edit">
                <TextField source="name" />
                <CampaignStatusField source="status" sortable={false} />
                <DateField source="created" showTime locales="fi-FI" sortable={false} />
                <DateField source="start_date" showTime locales="fi-FI" />
                <DateField source="end_date" showTime locales="fi-FI" />
                <TextField source="target_count" />
                <CampaignResponseRateField
                    label="resources.campaign.fields.response_rate"
                    sortable={false}
                />
                <TextField source="accepted_count" />
                <TextField source="rejected_count" />
                <FunctionField
                    label="resources.campaign.fields.export_schedule"
                    render={record =>
                        record?.public?.settings?.csv_schedule
                            ? `${formatCampaignWeekdays(record.public.settings.csv_schedule.weekdays)} / ${record.public.settings.csv_schedule.time}`
                            : translate('resources.campaign.manual_export')
                    }
                />
                <CampaignExportButton email />
                <CampaignWorkbookButton />
            </Datagrid>
        </List>
    );
};

export default CampaignList;
